import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { consoleError } from "./console";
import joi from "joi";

type HTTP_METHOD = "get" | "post" | "put" | "delete";
type REQUEST = {
  baseURL?: string;
  params?: {
    [key: string]: any;
  };
  query?: {
    [key: string]: any;
  };
  body?: {
    [key: string]: any;
  };
};

export class API<
  REQUEST_TYPE extends REQUEST,
  RESPONSE_TYPE,
  PARSED_RESPONSE_TYPE = RESPONSE_TYPE
> {
  serverInstance: Server | null;
  url: string;
  method: HTTP_METHOD;
  requestValidator: joi.Schema;
  responseParser?: (response: RESPONSE_TYPE) => PARSED_RESPONSE_TYPE;
  constructor(args: {
    serverInstance?: Server;
    url: string;
    method: HTTP_METHOD;
    requestValidator?: joi.Schema;
    responseParser?: (response: RESPONSE_TYPE) => PARSED_RESPONSE_TYPE;
  }) {
    this.serverInstance = args.serverInstance || null;
    this.url = args.url;
    this.method = args.method;
    this.requestValidator = args.requestValidator ?? joi.any();
    this.responseParser = args.responseParser;
  }

  makePathWithParams(path: string, params: any) {
    if (!params) {
      return path;
    }
    return Object.keys(params).reduce((result: string, key) => {
      return result.replace(`:${key}`, params[key]);
    }, path);
  }

  isEmptyObject(obj: any) {
    if (!obj) {
      return true;
    }
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  filterObject(obj: any) {
    return Object.keys(obj).reduce((result: any, key) => {
      if (obj[key] === undefined || obj[key] === null) {
        return result;
      }
      result[key] = obj[key];
      return result;
    }, {});
  }

  logValidationError(args: any, error: any) {
    console.group(":: API ERROR ::");
    console.log(`URL\t\t:: ${this.serverInstance?.baseURL}${this.url}`);
    console.log(`METHOD\t:: ${this.method}`);
    console.log("ARGS\t::", args);
    console.log("ERROR\t::", error);
    console.groupEnd();
  }

  async request(args?: REQUEST_TYPE): Promise<PARSED_RESPONSE_TYPE> {
    if (!this.serverInstance) {
      throw new Error("serverInstance is not defined");
    }

    const { value: requestValue, error: requestError } =
      this.requestValidator.validate(args);

    if (requestError) {
      this.logValidationError(args, requestError);
      throw requestError;
    }

    let requestObj: any = {
      url: this.url,
      method: this.method,
    };

    if (!this.isEmptyObject(requestValue?.params)) {
      requestObj.url = this.makePathWithParams(this.url, requestValue.params);
    }

    if (!this.isEmptyObject(requestValue?.query)) {
      requestObj.params = requestValue.query;
    }

    if (!this.isEmptyObject(requestValue?.body)) {
      requestObj.body = requestValue.body;
    }

    const response = (await this.serverInstance.request(
      requestObj
    )) as RESPONSE_TYPE;

    if (!this.responseParser) {
      return response as unknown as PARSED_RESPONSE_TYPE;
    }

    return this.responseParser(response) as PARSED_RESPONSE_TYPE;
  }
}

export class Server {
  serverInstance: AxiosInstance;
  baseURL?: string;

  constructor(args: { baseURL?: string; timeout?: number }) {
    this.serverInstance = axios.create({
      baseURL: args?.baseURL,
      timeout: args?.timeout,
    });
    this.baseURL = args?.baseURL;
  }

  updateRequestHeader(requestHeader: any) {
    this.serverInstance.defaults.headers.common = requestHeader;
  }

  checkRequestHeader() {
    console.log(this.serverInstance.defaults);
  }

  serverResponseInterceptor() {
    this.serverInstance.interceptors.response.use(
      function (response: AxiosResponse) {
        return response;
      },
      function (error: AxiosError) {
        consoleError(error);
        throw error;
      }
    );
  }

  async request(args: any) {
    try {
      return (await this.serverInstance(args)).data;
    } catch (e) {
      console.log(e);
    }
  }
}
